import { getMonthsElapsed, startOfMonthUTC, toDate } from './dates.js';
import {
  getSegmentGroupKey,
  groupSegmentsByLineItem,
  resolveActiveSegment,
  type SegmentLike,
} from './segments.js';
import type { AssetEventInput } from './types.js';

/**
 * Normalize appreciation rate to a monthly decimal multiplier.
 * - DB / EventForm store decimals (0.06 = 6% per month)
 * - Legacy or test data may use percentage points (6 = 6% per month)
 */
export function normalizeMonthlyAppreciationRate(rate: number): number {
  return Math.abs(rate) > 1 ? rate / 100 : rate;
}

export function shouldApplyAssetPurchaseCashFlow(
  asset: Pick<AssetEventInput, 'segmentKind' | 'suppressPurchaseCashFlow'>,
): boolean {
  if (asset.suppressPurchaseCashFlow) return false;
  if (asset.segmentKind === 'revision') return false;
  return true;
}

export function calculateAssetValue(asset: AssetEventInput, currentDate: Date): number {
  if (!asset.amount || asset.monthlyAppreciationRate === undefined) {
    return asset.amount || 0;
  }

  const startDate = toDate(asset.date);
  if (!startDate) return asset.amount || 0;

  const monthsElapsed = Math.max(0, getMonthsElapsed(startDate, currentDate));
  let currentValue = asset.amount;
  const minimumValue = asset.minimumValue ?? 0;
  const rate = normalizeMonthlyAppreciationRate(asset.monthlyAppreciationRate);

  for (let i = 0; i < monthsElapsed; i++) {
    if (currentValue <= minimumValue && rate < 0) break;
    currentValue += currentValue * rate;
    if (rate < 0 && currentValue < minimumValue) {
      currentValue = minimumValue;
      break;
    }
  }

  return Math.max(minimumValue, currentValue);
}

/** Monthly P&L impact (appreciation positive, depreciation negative) */
export function calculateAssetMonthlyImpact(asset: AssetEventInput, targetMonth: Date): number {
  if (!asset.amount || asset.monthlyAppreciationRate === undefined) return 0;

  const startDate = toDate(asset.date);
  if (!startDate) return 0;

  const assetStartMonth = startOfMonthUTC(startDate);
  const target = startOfMonthUTC(targetMonth);
  if (target.getTime() < assetStartMonth.getTime()) return 0;

  const valueAtMonthStart = calculateAssetValue(asset, target);
  const minimumValue = asset.minimumValue ?? 0;
  if (valueAtMonthStart <= minimumValue && asset.monthlyAppreciationRate < 0) return 0;

  return valueAtMonthStart * normalizeMonthlyAppreciationRate(asset.monthlyAppreciationRate);
}

/** Cash outflow when purchasing an asset (0 if not acquisition month or revision segment) */
export function calculateAssetPurchaseCashFlow(asset: AssetEventInput, targetMonth: Date): number {
  if (!asset.amount || !shouldApplyAssetPurchaseCashFlow(asset)) return 0;
  const startDate = toDate(asset.date);
  if (!startDate) return 0;
  const acquisitionMonth = startOfMonthUTC(startDate);
  const target = startOfMonthUTC(targetMonth);
  if (acquisitionMonth.getTime() !== target.getTime()) return 0;
  return -asset.amount;
}

/** Book value of asset at target date for balance-sheet style cash (no double-count with monthly P&L) */
export function calculateAssetBookValueAtDate(asset: AssetEventInput, targetDate: Date): number {
  const startDate = toDate(asset.date);
  if (!startDate || !asset.amount) return 0;
  if (startOfMonthUTC(targetDate).getTime() < startOfMonthUTC(startDate).getTime()) return 0;
  return calculateAssetValue(asset, targetDate);
}

type AssetSegmentEvent = AssetEventInput &
  SegmentLike & {
    id: string;
    type?: string;
  };

function toAssetSegmentEvent(event: AssetEventInput & { id?: string }): AssetSegmentEvent | null {
  if (event.type !== 'asset' || !event.id) return null;
  return event as AssetSegmentEvent;
}

/** One active asset segment per logical line item for the target month. */
export function resolveActiveAssetEvents<T extends AssetEventInput & { id?: string; type?: string }>(
  events: T[],
  targetMonth: Date,
): T[] {
  const assetSegments = events
    .map((event) => toAssetSegmentEvent(event))
    .filter((event): event is AssetSegmentEvent => event !== null);

  const groups = groupSegmentsByLineItem(assetSegments);
  const active: T[] = [];

  for (const segments of groups.values()) {
    const segment = resolveActiveSegment(segments, targetMonth);
    if (segment) {
      active.push(segment as T);
    }
  }

  return active;
}

/** Modeled book value for a segment chain at a date (single group). */
export function calculateGroupedAssetBookValueAtDate(
  events: Array<AssetEventInput & { id?: string; type?: string }>,
  targetDate: Date,
): number {
  const active = resolveActiveAssetEvents(events, targetDate);
  return active.reduce((sum, asset) => sum + calculateAssetBookValueAtDate(asset, targetDate), 0);
}

export function getAssetLineItemGroupId(
  event: Pick<AssetEventInput, 'lineItemGroupId' | 'id'>,
): string {
  return getSegmentGroupKey({
    id: event.id ?? '',
    lineItemGroupId: event.lineItemGroupId,
  });
}
